/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { LoanDetail } from '@shared/model/loandetail.model';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { DataLoadURL } from '@workflow/ecp-shared/model/dataload-url';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { Result } from 'src/shared/model/result';

interface ApiCache {
  /** api data */
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  data: LoanDetail;
  /**
   *  when true, a copy of existing api data is returned. If false, the next cache access will trigger
   *  a new api call to retrieve fresh data.
   */
  serveCachedData: boolean;
}

/**
 * Provides cache service
 */
@Injectable({
  providedIn: 'root'
})
export class ApiCacheService {
  /**
   * registry to store cached data accessible via cache key
   */
  private _registry: { [cacheKey: string]: ApiCache } = {};

  constructor(private readonly api: ApiService, private readonly storageWrapperService: StorageWrapperService) {}

  /**
   * Provide a cached or fresh copy of api data
   * @param cacheKey : key to retrieve cached data
   * @param apiUrl : api path
   * @param cb : successful callback
   * @param cbError : on error callback fn
   * @param useCachedData : flag to invalidate cache. Default to true. If value is true, a cached copy of api
   * data is served. Otherwise request a fresh api call for data.
   */
  public retrieveData(
    cacheKey: string,
    apiUrl: DataLoadURL,
    cb: (loanDetails: LoanDetail) => void,
    cbError?: (error) => void,
    useCachedData = true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any
  ): void {
    const loanData = this._registry[cacheKey] && this._registry[cacheKey].data;

    if (loanData && this._registry[cacheKey].serveCachedData && useCachedData) {
      cb(loanData);
    } else {
      this.api
        .request({
          urlObj: apiUrl,
          body
        })
        .toPromise()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: Result<any>): void => {
          const loanDetail = res.data;
          this._registry[cacheKey] = { data: loanDetail, serveCachedData: true };
          cb(loanDetail);
        })
        .catch((error) => {
          cbError(error);
        });
    }
  }

  /**
   * Sets cache flag to false to force an api request for fresh data.
   * @param cacheKey : optional parameter, default to current appNumber if not provided.
   */
  public invalidateCache(cacheKey?: string): void {
    const id = cacheKey || this.storageWrapperService.get<string>(Info.appNumber);
    if (this._registry[id]) {
      this._registry[id].serveCachedData = false;
    }
  }

  /**
   * Purge all data in cache instance (ex. when log out)
   * @param callback callback fn to be called after cache cleared.
   */
  public clear(callback?: () => void): void {
    this._registry = {};
    if (typeof callback === 'function') {
      callback();
    }
  }
}
